/**
 * TutorialManager — Gère les tutoriels pour QuickyGame.
 * Lit la méthode statique getPlayback() de chaque classe de micro-jeu.
 * Ne dépend d'aucune logique de gameplay : c'est une pure feature UI/menu.
 *
 * Supporte le mode "playback" via PlaybackManager pour les jeux
 * qui définissent des actions — affiche une démo animée avec curseur fantôme.
 * Pour les jeux sans actions, affiche le jeu en fond avec les instructions.
 */
import PlaybackManager from './PlaybackManager.js';

export default class TutorialManager {
    constructor(engine) {
        this.engine = engine;
        this.i18n = engine.i18n;
        this._overlay = null;
        this._currentOnPlay = null;
        this._currentOnClose = null;
        this.playbackManager = new PlaybackManager(engine);
    }

    /**
     * Show a playback tutorial if available, otherwise fall back to classic modal.
     * This is the preferred entry point when called from game flow (e.g. on failure).
     * @param {Function} GameClass - The microgame class
     * @param {Function} onResume - Callback when tutorial finishes
     * @param {Function|null} onClose - Callback if closed (unused in playback, kept for compat)
     * @param {Object} options - { hideClose, gameMode, timerOverride }
     */
    showWithPlayback(GameClass, onResume = null, onClose = null, options = {}) {
        // Try playback first, and NEVER fallback since we migrated all games
        this.playbackManager.show(GameClass, onResume, options);
    }

    /**
     * Affiche le tutoriel d'un jeu.
     * @param {Function} GameClass - La classe statique du micro-jeu
     * @param {Function|null} onPlay - Callback appelé si le joueur clique JOUER
     * @param {Function|null} onClose - Callback appelé si le joueur clique FERMER ou clique à côté
     * @param {Object} options - Options supplémentaires (ex: { hideClose: true })
     */
    show(GameClass, onPlay = null, onClose = null, options = {}) {
        this.hide(); // Clean up any existing modal

        const tutorial = (typeof GameClass.getPlayback === 'function')
            ? GameClass.getPlayback()
            : null;

        const lang = this.i18n.getLanguage();
        const getText = (obj) => {
            if (!obj) return '';
            if (typeof obj === 'string') return obj;
            return obj[lang] || obj['en'] || obj['fr'] || '';
        };

        const t = (key) => this.i18n.t(`tutorial.${key}`);

        this._currentOnPlay = onPlay;
        this._currentOnClose = onClose;

        // Build modal
        const overlay = document.createElement('div');
        overlay.className = 'tutorial-overlay';
        overlay.id = 'tutorial-overlay';

        const modal = document.createElement('div');
        modal.className = 'tutorial-modal';

        // Icon / visual
        const iconEl = document.createElement('div');
        iconEl.className = 'tutorial-modal-icon';
        if (tutorial && tutorial.icon) {
            iconEl.innerHTML = tutorial.icon;
        } else {
            iconEl.innerHTML = this._defaultIcon();
        }

        // Title
        const titleEl = document.createElement('div');
        titleEl.className = 'tutorial-modal-title';
        titleEl.textContent = tutorial ? getText(tutorial.title) : (GameClass.name || '');

        // Controls section
        const controlsSection = document.createElement('div');
        controlsSection.className = 'tutorial-section';

        const controlsLabel = document.createElement('div');
        controlsLabel.className = 'tutorial-section-label';
        controlsLabel.textContent = t('controls');

        const controlsText = document.createElement('div');
        controlsText.className = 'tutorial-section-text';
        controlsText.textContent = tutorial ? getText(tutorial.controls) : '—';

        controlsSection.appendChild(controlsLabel);
        controlsSection.appendChild(controlsText);

        // Objective section
        const objectiveSection = document.createElement('div');
        objectiveSection.className = 'tutorial-section';

        const objectiveLabel = document.createElement('div');
        objectiveLabel.className = 'tutorial-section-label';
        objectiveLabel.textContent = t('objective');

        const objectiveText = document.createElement('div');
        objectiveText.className = 'tutorial-section-text';
        objectiveText.textContent = tutorial ? getText(tutorial.objective) : '—';

        objectiveSection.appendChild(objectiveLabel);
        objectiveSection.appendChild(objectiveText);

        // Buttons
        const btnRow = document.createElement('div');
        btnRow.className = 'tutorial-btn-row';

        const closeBtn = document.createElement('button');
        closeBtn.className = 'neon-btn small tutorial-close-btn';
        closeBtn.textContent = t('close');
        closeBtn.id = 'tutorial-close-btn';
        closeBtn.addEventListener('click', () => this.hide());

        if (!options.hideClose) {
            btnRow.appendChild(closeBtn);
        }

        if (onPlay) {
            const playBtn = document.createElement('button');
            playBtn.className = 'neon-btn tutorial-play-btn';
            playBtn.textContent = t('play');
            playBtn.id = 'tutorial-play-btn';
            playBtn.addEventListener('click', () => {
                this.hide(true); // wasPlayClicked = true
            });
            btnRow.appendChild(playBtn);
        }

        // Assemble
        modal.appendChild(iconEl);
        modal.appendChild(titleEl);
        modal.appendChild(controlsSection);
        modal.appendChild(objectiveSection);
        modal.appendChild(btnRow);
        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        this._overlay = overlay;

        // Close on overlay click (outside modal)
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay && !options.hideClose) this.hide();
        });

        // Animate in
        requestAnimationFrame(() => {
            overlay.classList.add('active');
        });
    }

    hide(wasPlayClicked = false) {
        if (!this._overlay) return;

        const onPlay = this._currentOnPlay;
        const onClose = this._currentOnClose;

        this._overlay.classList.remove('active');
        this._overlay.classList.add('closing');
        const el = this._overlay;
        setTimeout(() => {
            if (el && el.parentNode) el.remove();
        }, 300);

        this._overlay = null;
        this._currentOnPlay = null;
        this._currentOnClose = null;

        if (wasPlayClicked) {
            if (onPlay) onPlay();
        } else {
            if (onClose) onClose();
        }
    }

    _defaultIcon() {
        // Generic gamepad icon SVG
        return `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none"
            stroke="var(--primary-color)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="7" width="20" height="10" rx="4"/>
            <path d="M7 12h2m-1-1v2"/>
            <circle cx="16" cy="11.5" r="0.5" fill="var(--primary-color)"/>
            <circle cx="17.5" cy="12.5" r="0.5" fill="var(--primary-color)"/>
        </svg>`;
    }
}
